import {
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  FormControl,
  FormLabel,
  Heading,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { useFetchAfeDetailsQuery } from 'api';
import { PostAfeTransactionLineItemRequest } from 'api/bindings/PostAfeTransactionLineItemRequest';
import { SubAccountResponse } from 'api/bindings/SubAccountResponse';
import Decimal from 'decimal.js';
import {
  AppInput,
  AutoComplete,
  CurrencyLabel,
  FormButtonGroup,
  MultiBar,
} from 'features';
import { useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

interface Option {
  _id: number;
  subAccountId: string;
  subAccount: SubAccountResponse;
  totalBudget: string;
  totalContingency: string;
  totalCommitment: string;
  pendingCommitment: string;
  totalInvoice: string;
  pendingInvoice: string;
}

interface Form {
  _id: number;
  budget: string;
  contingency: string;
}
// interface Row extends AfeTransactionLineItemModel {}
interface Row extends PostAfeTransactionLineItemRequest {}
export interface AfeBudgetRevisionAccountDrawerProps
  extends Omit<DrawerProps, 'children' | 'onSubmit'> {
  afeId: string | undefined;
  row: Row;
  onSubmit: (row: Row) => void;
}

export const AfeBudgetRevisionAccountDrawer = (
  props: AfeBudgetRevisionAccountDrawerProps
) => {
  const { afeId, row, ...others } = props;

  const { data: afe } = useFetchAfeDetailsQuery(afeId ?? "0", {
    skip: !afeId,
  });
  const options = useMemo(() => {
    let id = 0;
    return afe?.eacLineItems.reduce((sum, n) => {
      return [...sum, { _id: id++, ...n } as Option];
    }, [] as Option[]);
  }, [afe?.eacLineItems]);
  const preSelectedOption = useMemo(
    () =>
      (row.subAccountId &&
        options?.find((x) => x.subAccountId === row.subAccountId)) ||
      undefined,
    [options, row.subAccountId]
  );

  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      _id: preSelectedOption?._id,
      budget: row.budgetedAmount,
      contingency: row.contingencyAmount,
    },
  });

  const selectedOptionId = form.watch('_id');
  const budgetAmount = form.watch('budget');
  const contingencyAmount = form.watch('contingency');

  const currentOption = useMemo(
    () => options?.find((x) => x._id === selectedOptionId),
    [options, selectedOptionId]
  );

  const data = useMemo(() => {
    const budgetTotal = new Decimal(budgetAmount || 0).add(
      contingencyAmount || 0
    );
    const remainingCommitment = budgetTotal
      .sub(currentOption?.totalCommitment || 0)
      .sub(currentOption?.pendingCommitment || 0);
    const remainingInvoice = budgetTotal
      .sub(currentOption?.totalInvoice || 0)
      .sub(currentOption?.pendingInvoice || 0);
    return {
      budgetTotal: budgetTotal.toNumber(),
      remainingCommitment: remainingCommitment.toNumber(),
      remainingInvoice: remainingInvoice.toNumber(),
    };
  }, [
    budgetAmount,
    contingencyAmount,
    currentOption?.pendingCommitment,
    currentOption?.pendingInvoice,
    currentOption?.totalCommitment,
    currentOption?.totalInvoice,
  ]);

  const onSubmit = useCallback(
    (form: Form) => {
      if (!currentOption) return;
      props.onSubmit({
        ...row,
        subAccountId: currentOption.subAccountId,
        budgetedAmount: form.budget,
        contingencyAmount: form.contingency,
      });
      props.onClose();
    },
    [currentOption, props, row]
  );

  return (
    <Drawer {...others} size="xl">
      <FormProvider {...form}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>
            <Heading size="xs">AFE Budget Revision</Heading>
          </DrawerHeader>
          <DrawerBody>
            <Stack spacing={[5]}>
              <FormControl>
                <FormLabel htmlFor="account">Account</FormLabel>
                <Controller
                  control={form.control}
                  name="_id"
                  rules={{ min: 0 }}
                  render={(p) => (
                    <AutoComplete
                      id="accounts"
                      items={options}
                      valueKey={(x) => x._id}
                      textKey={(x) => x.subAccount.fullSubAccountName}
                      {...p.field}
                    />
                  )}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="budget">Budget</FormLabel>
                <Controller
                  control={form.control}
                  name="budget"
                  rules={{ min: 0 }}
                  render={(p) => (
                    <AppInput id="budget" mode="currency" {...p.field} />
                  )}
                />
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="contingency">Contingency</FormLabel>
                <Controller
                  control={form.control}
                  name="contingency"
                  rules={{ min: 0 }}
                  render={(p) => <AppInput mode="currency" {...p.field} />}
                />
              </FormControl>
              <SimpleGrid columns={2} spacing={4}>
                <Text as="b">Budget / Contingency</Text>
                <MultiBar values={[budgetAmount, contingencyAmount]} />
                <Text as="b">Budget:</Text>
                <CurrencyLabel value={budgetAmount} />
                <Text as="b">Contingency:</Text>
                <CurrencyLabel value={contingencyAmount} />
                <Text as="b">Total:</Text>
                <CurrencyLabel value={[budgetAmount, contingencyAmount]} />
                <Divider />
                <Divider />
                <Text as="b">Commitments:</Text>
                <MultiBar
                  max={[data.budgetTotal]}
                  values={[
                    currentOption?.totalCommitment,
                    currentOption?.pendingCommitment,
                  ]}
                />
                <Text as="b"></Text>
                <CurrencyLabel
                  value={new Decimal(currentOption?.totalCommitment || 0).add(
                    currentOption?.pendingCommitment || 0
                  )}
                />
                <Text as="b">Invoiced:</Text>
                <MultiBar
                  max={[data.budgetTotal]}
                  values={[
                    currentOption?.totalInvoice,
                    currentOption?.pendingInvoice,
                  ]}
                />
                <Text as="b"></Text>
                <CurrencyLabel
                  value={new Decimal(currentOption?.totalInvoice || 0).add(
                    currentOption?.pendingInvoice || 0
                  )}
                />
              </SimpleGrid>
              <SimpleGrid columns={2} spacing={4}>
                <Text as="b">Available to Commit:</Text>
                <CurrencyLabel value={data.remainingCommitment} />

                <Text as="b">Available to Invoice:</Text>
                <CurrencyLabel value={data.remainingInvoice} />
              </SimpleGrid>
            </Stack>
          </DrawerBody>
          <DrawerFooter>
            <FormButtonGroup
              submitCaption="Save Row"
              onCancel={props.onClose}
              onConfirm={form.handleSubmit(onSubmit)}
            />
          </DrawerFooter>
        </DrawerContent>
      </FormProvider>
    </Drawer>
  );
};
